import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, TrendingUp } from "lucide-react";
import Reveal from "../components/ui/Reveal";
import PhoneMockup from "../components/ui/PhoneMockup";
import FeatureStories from "../components/home/FeatureStories";
import { YouAskedWeBuilt, ToolkitGrid } from "../components/home/HomeSections";
import SEO, { generateMobileAppSchema, generateOrganizationSchema } from "../components/seo/SEO";
import { useAppDownload } from "../context/AppDownloadContext";
import home1Screen    from "../assets/images/home1.svg";
import home2Screen    from "../assets/images/headerimage/home.svg";
import studyScreen    from "../assets/images/headerimage/study.svg";
import examprep    from "../assets/images/examprep.svg";
import outliner    from "../assets/images/headerimage/outliner.svg";
import communityScreen from "../assets/images/headerimage/community.svg";

const stats = [
  { value: "1K+", label: "Active Students" },
  { value: "4-7", label: "Years Retention" },
  { value: "2", label: "Life Stages" },
  { value: "15+", label: "Core Features" },
];

// Two rows  pre-uni (green) and university (varied) for the brand strip
const preUniTags = ["JAMB Prep", "WAEC Practice", "CBT Simulator", "AI Questions", "Study Streaks", "Eligibility Checker"];
const uniTags    = ["Digital Student ID", "GPA Tracker", "Alumni Network", "Academic Calendar", "Campus Safety", "Scholarship Alerts", "AI Writing Suite", "Study Partners"];

const journeyPhases = [
  {
    number: "01",
    eyebrow: "Exam Prep",
    title: "Wanting to Get In",
    description: "JAMB & WAEC preparation with past questions, CBT simulation, AI study support, and gamified learning.",
  },
  {
    number: "02",
    eyebrow: "Transition",
    title: "The Bridge Moment",
    description: "Seamless transition from exam prep to university mode. Same app, same account, new chapter.",
  },
  {
    number: "03",
    eyebrow: "Campus Life",
    title: "Getting Through University",
    description: "Academic calendar, GPA tracker, Digital Student ID, alumni mentorship, campus safety, scholarship alerts, all in one verified place.",
  },
];

const testimonials = [
  {
    quote: "CampusGenie helped me score 298 in JAMB. The CBT practice was exactly like the real exam, and the AI study planner kept me on track. I'm so glad I found this app!",
    author: "Chisom A.",
    role: "University of Lagos, 100L",
    avatar: "C",
  },
  {
    quote: "The GPA tracker is something I didn't know I needed. I can see my CGPA every semester without calculating manually. And the Digital Student ID saved me at the library gate.",
    author: "Emeka O.",
    role: "OAU, 300L",
    avatar: "E",
  },
  {
    quote: "The Alumni network connected me to two internship leads within a week. I wasn't expecting that from a study app. CampusGenie is genuinely built for everything.",
    author: "Fatima M.",
    role: "ABU Zaria, 400L",
    avatar: "F",
  },
];

// Custom spring easing for phone float
const FLOAT_EASE = "easeInOut";

export default function HomePage() {
  const { openAppModal } = useAppDownload();
  const homeStructuredData = [
    generateOrganizationSchema(),
    generateMobileAppSchema(),
  ];

  return (
    <>
      <SEO
        title="Exam Prep to University Graduation - Student Success App"
        description="Challenge your friends, keep your daily streak and practise real JAMB CBT questions. CampusGenie stays with you into university with class groups, GPA tracking, a digital student ID and AI study tools."
        url="/"
        structuredData={homeStructuredData}
      />

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              
              <h1 className="heading-hero hero-title" style={{ fontSize: "clamp(2.2rem, 4.4vw, 3.5rem)" }}>
                Challenge your friends. Keep your streak. <span className="text-gradient">Get in</span>, then <span className="text-gradient">get through</span>.
              </h1>

              <p className="hero-subtitle">
                Head-to-head challenges, daily tests, streaks and real JAMB CBT practice. Then, once you're in university, a class group that keeps your whole programme organised.
              </p>

              <div className="hero-actions">
                <button type="button" className="btn btn-primary btn-lg" onClick={openAppModal}>
                  Download the App
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
                <Link to="/product" className="btn btn-secondary btn-lg">
                  See How It Works
                </Link>
              </div>

              <div className="hero-stats">
                {stats.map((stat) => (
                  <div key={stat.label} className="hero-stat">
                    <span className="hero-stat-value">{stat.value}</span>
                    <span className="hero-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Hero Visual  Z-axis phone depth with float animations */}
            <motion.div
              className="hero-visual"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="hero-visual-bg" />

              {/* Back phone  adds depth behind the main phone */}
              <motion.div
                className="hero-phone-back"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 0.55, x: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
              >
                <div className="hero-phone-back-inner">
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: FLOAT_EASE, delay: 1 }}
                  >
                    <PhoneMockup image={home2Screen} alt="Study Tab" size="default" />
                  </motion.div>
                </div>
              </motion.div>

              {/* Main centre phone  floats */}
              <div className="hero-phone" style={{ position: "relative", zIndex: 2 }}>
                <motion.div
                  animate={{ y: [0, -14, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: FLOAT_EASE }}
                >
                  <PhoneMockup image={home1Screen} alt="CampusGenie Home" size="large" />
                </motion.div>
              </div>

              {/* Floating chip  JAMB score */}
              <motion.div
                className="hero-floating-card hero-floating-card-1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
              >
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 4.5, repeat: Infinity, ease: FLOAT_EASE, delay: 0.3 }}
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div style={{
                    width: 40, height: 40,
                    background: "var(--gradient-primary)",
                    borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "white",
                  }}>
                    <Target size={22} strokeWidth={2} aria-hidden />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>JAMB Score</div>
                    <div style={{ color: "var(--color-primary)", fontWeight: 700 }}>337/400</div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Floating chip  GPA (university mode) */}
              <motion.div
                className="hero-floating-card hero-floating-card-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.75, duration: 0.5 }}
              >
                <motion.div
                  animate={{ y: [0, -7, 0] }}
                  transition={{ duration: 5.2, repeat: Infinity, ease: FLOAT_EASE, delay: 0.8 }}
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div style={{
                    width: 40, height: 40,
                    background: "var(--color-primary-soft)",
                    borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--color-primary)",
                  }}>
                    <TrendingUp size={22} strokeWidth={2} aria-hidden />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>GPA Tracker</div>
                    <div style={{ color: "var(--color-text-secondary)", fontSize: "13px" }}>4.12 / 5.0 this semester</div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Strip  infinite scrolling marquee */}
      <section className="brand-strip">
        <div className="container">
          <div className="brand-strip-inner">
            <span className="brand-strip-label">Built for the Nigerian Student Journey</span>
            {/* Tags are duplicated so the loop is seamless at -50% translateX */}
            <div className="brand-marquee">
              <div className="brand-marquee-track">
                {preUniTags.map((tag) => (
                  <span key={`a-${tag}`} className="brand-tag">{tag}</span>
                ))}
                {uniTags.map((tag) => (
                  <span key={`a-uni-${tag}`} className="brand-tag brand-tag--uni">{tag}</span>
                ))}
                {/* Duplicate set  makes the loop seamless */}
                {preUniTags.map((tag) => (
                  <span key={`b-${tag}`} className="brand-tag">{tag}</span>
                ))}
                {uniTags.map((tag) => (
                  <span key={`b-uni-${tag}`} className="brand-tag brand-tag--uni">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What makes CampusGenie different: the features students talk about */}
      <FeatureStories />

      <YouAskedWeBuilt />

      {/* Journey Section */}
      <section className="section journey-section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">The Bridge Strategy</span>
              <h2 className="heading-2">
                Most exam apps die after the exam.<br />CampusGenie doesn't.
              </h2>
              <p className="body-large">
                We built CampusGenie around a simple insight: the same student who needs JAMB prep today will need university tools tomorrow. Why make them start over?
              </p>
            </div>
          </Reveal>

          <div className="journey-visual-grid">
            <div className="journey-content">
              <div className="journey-phases">
                {journeyPhases.map((phase, index) => (
                  <Reveal key={phase.number} delay={index * 0.1}>
                    <div className="journey-phase">
                      <div className="journey-phase-number">{phase.number}</div>
                      <div className="journey-phase-content">
                        <span className="journey-phase-eyebrow">{phase.eyebrow}</span>
                        <h4>{phase.title}</h4>
                        <p>{phase.description}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Journey phones  Z-axis cascade with float animations */}
            <Reveal delay={0.2}>
              <div className="journey-visual-phones">
                <motion.div
                  className="phone-mockup-1"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 5.5, repeat: Infinity, ease: FLOAT_EASE }}
                >
                  <PhoneMockup image={studyScreen} alt="Study Tab" size="small" />
                </motion.div>
                <motion.div
                  className="phone-mockup-2"
                  animate={{ y: [0, -14, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: FLOAT_EASE, delay: 0.5 }}
                >
                  <PhoneMockup image={home1Screen} alt="CampusGenie Home" size="default" />
                </motion.div>
                <motion.div
                  className="phone-mockup-3"
                  animate={{ y: [0, -9, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: FLOAT_EASE, delay: 1 }}
                >
                  <PhoneMockup image={communityScreen} alt="Community Tab" size="small" />
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Everything else in the app */}
      <ToolkitGrid />

      {/* Showcase Section  Pre-University */}
      <section className="section showcase-section">
        <div className="container">
          <div className="showcase-grid">
            <Reveal>
              <div className="showcase-content">
                <span className="eyebrow">Pre-University Excellence</span>
                <h2 className="heading-2">
                  Ace JAMB and WAEC with tools built for serious practice.
                </h2>
                <p className="body-large">
                  CampusGenie transforms exam prep into an engaging, structured experience. Practice with real past questions, simulate the actual CBT environment, and track your progress with AI-powered insights.
                </p>
                <div className="showcase-list">
                  <div className="showcase-list-item">10+ years of JAMB & WAEC past questions organised by subject and year</div>
                  <div className="showcase-list-item">Authentic CBT simulation that mirrors the real exam experience</div>
                  <div className="showcase-list-item">AI study planner that creates personalised schedules based on your weak areas</div>
                  <div className="showcase-list-item">Gamified challenges and streaks that keep you motivated daily</div>
                </div>
                <Link to="/students" className="btn btn-primary" style={{ marginTop: "24px" }}>
                  Explore Pre-Uni Features
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="showcase-phone-wrap">
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 6.5, repeat: Infinity, ease: FLOAT_EASE }}
                >
                  <PhoneMockup image={examprep} alt="Study Tab" size="large" />
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Showcase Section  University */}
      <section className="section section-dark showcase-section">
        <div className="container">
          <div className="showcase-grid showcase-grid-reverse">
            <Reveal>
              <div className="showcase-content">
                <span className="eyebrow">University Companion</span>
                <h2 className="heading-2" style={{ color: "white" }}>
                  Your class, organised. Your campus life, in one app.
                </h2>
                <p className="body-large">
                  Once you're in, CampusGenie gets you into a group for your programme and entry year, with an elected course rep, and puts the rest of university life around it.
                </p>
                <div className="showcase-list">
                  <div className="showcase-list-item">A group for your programme and set, with pinned announcements, the exam timetable and past papers in one place</div>
                  <div className="showcase-list-item">Vote for your course rep, message classmates directly and find study partners</div>
                  <div className="showcase-list-item">Digital Student ID and a GPA tracker for your CGPA across every semester</div>
                  <div className="showcase-list-item">Scholarship alerts, internships and alumni mentors when you're ready for what's next</div>
                </div>
                <Link to="/students" className="btn btn-primary" style={{ marginTop: "24px" }}>
                  Explore University Features
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="showcase-phone-wrap showcase-phone-wrap--align-start showcase-phone-wrap--shift">
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: FLOAT_EASE, delay: 0.5 }}
                >
                  <PhoneMockup image={outliner} alt="Community Tab" size="large" />
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="section social-proof-section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Student Stories</span>
              <h2 className="heading-2">
                What students say about CampusGenie.
              </h2>
            </div>
          </Reveal>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Reveal key={testimonial.author} delay={index * 0.1}>
                <div className="testimonial-card">
                  <p className="testimonial-quote">"{testimonial.quote}"</p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">{testimonial.avatar}</div>
                    <div>
                      <div className="testimonial-name">{testimonial.author}</div>
                      <div className="testimonial-role">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section  full-bleed closing statement */}
      <section className="cta-section-full">
        <div className="container">
          <Reveal>
            <div className="cta-content">
              <span className="eyebrow">Get Started</span>
              <h2 className="heading-2">
                Ready to beat your friends at studying?
              </h2>
              <p className="body-large">
                Free to download on iOS and Android. Start a challenge, take today's daily test and keep your streak going.
              </p>
              <div className="cta-buttons">
                <button type="button" className="btn btn-primary btn-lg" onClick={openAppModal}>
                  Download CampusGenie
                </button>
                <Link to="/product" className="btn btn-secondary btn-lg">
                  Learn More
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
